import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import CharacterCount from "@tiptap/extension-character-count";
import { useEffect } from "react";
import {
  Bold, Italic, Strikethrough, Underline as UnderlineIcon, Heading1, Heading2, Heading3,
  List, ListOrdered, Quote, Code, Undo2, Redo2, Link as LinkIcon, Image as ImageIcon, Minus,
  AlignLeft, AlignCenter, AlignRight,
} from "lucide-react";

function ToolbarButton({ onClick, active, disabled, title, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      data-testid={testId}
      className={`p-2 rounded-md text-stone-700 hover:bg-stone-200/70 transition disabled:opacity-40 disabled:cursor-not-allowed ${active ? "bg-stone-200 text-[#064E3B]" : ""}`}
    >
      {children}
    </button>
  );
}

export default function RichTextEditor({
  value,
  onChange,
  placeholder = "Start writing…",
  variant = "article", // "article" = full blog toolbar; "description" = description-specific
  maxChars,
}) {
  const isDescription = variant === "description";

  const extensions = [
    StarterKit.configure({
      heading: { levels: isDescription ? [1, 2, 3] : [2, 3] },
    }),
    Underline,
    TextAlign.configure({ types: ["heading", "paragraph"] }),
    Link.configure({
      openOnClick: false,
      HTMLAttributes: { class: "text-[#064E3B] underline underline-offset-2 hover:opacity-80" },
    }),
    Image.configure({
      HTMLAttributes: { class: "rounded-xl my-4 max-w-full h-auto" },
    }),
    Placeholder.configure({ placeholder }),
  ];
  if (maxChars) extensions.push(CharacterCount.configure({ limit: maxChars }));

  const editor = useEditor({
    extensions,
    content: value || "",
    onUpdate: ({ editor: ed }) => onChange(ed.getHTML()),
  });

  // Keep external value in sync when reopening
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (value !== current && (value || "") !== "") {
      editor.commands.setContent(value, false);
    } else if (!value) {
      editor.commands.clearContent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) return null;

  const addLink = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("Link URL", prev);
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    const safe = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    editor.chain().focus().extendMarkRange("link").setLink({ href: safe, target: "_blank", rel: "noopener noreferrer" }).run();
  };

  const addImage = () => {
    const url = window.prompt("Image URL (paste a hosted URL — to upload, use the cover image uploader above)");
    if (!url) return;
    editor.chain().focus().setImage({ src: url }).run();
  };

  const charCount = maxChars ? editor.storage.characterCount.characters() : 0;
  const atLimit = maxChars && charCount >= maxChars;

  return (
    <div className="rounded-lg border border-stone-300 bg-white" data-testid="rich-text-editor">
      <div className="flex flex-wrap items-center gap-0.5 border-b border-stone-200 px-2 py-1.5 bg-stone-50/60 rounded-t-lg">
        {isDescription && (
          <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive("heading", { level: 1 })} title="Heading 1" testId="rte-h1"><Heading1 className="w-4 h-4" /></ToolbarButton>
        )}
        <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} title="Heading 2" testId="rte-h2"><Heading2 className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })} title="Heading 3" testId="rte-h3"><Heading3 className="w-4 h-4" /></ToolbarButton>
        <div className="w-px h-5 bg-stone-200 mx-1" />
        <ToolbarButton onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} title="Bold (Ctrl+B)" testId="rte-bold"><Bold className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} title="Italic (Ctrl+I)" testId="rte-italic"><Italic className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive("underline")} title="Underline (Ctrl+U)" testId="rte-underline"><UnderlineIcon className="w-4 h-4" /></ToolbarButton>
        {!isDescription && (
          <ToolbarButton onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive("strike")} title="Strikethrough" testId="rte-strike"><Strikethrough className="w-4 h-4" /></ToolbarButton>
        )}
        <div className="w-px h-5 bg-stone-200 mx-1" />
        <ToolbarButton onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} title="Bullet list" testId="rte-ul"><List className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} title="Numbered list" testId="rte-ol"><ListOrdered className="w-4 h-4" /></ToolbarButton>
        {!isDescription && (
          <>
            <ToolbarButton onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive("blockquote")} title="Quote" testId="rte-quote"><Quote className="w-4 h-4" /></ToolbarButton>
            <ToolbarButton onClick={() => editor.chain().focus().toggleCodeBlock().run()} active={editor.isActive("codeBlock")} title="Code block" testId="rte-code"><Code className="w-4 h-4" /></ToolbarButton>
          </>
        )}
        <div className="w-px h-5 bg-stone-200 mx-1" />
        <ToolbarButton onClick={() => editor.chain().focus().setTextAlign("left").run()} active={editor.isActive({ textAlign: "left" })} title="Align left" testId="rte-align-left"><AlignLeft className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().setTextAlign("center").run()} active={editor.isActive({ textAlign: "center" })} title="Align center" testId="rte-align-center"><AlignCenter className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().setTextAlign("right").run()} active={editor.isActive({ textAlign: "right" })} title="Align right" testId="rte-align-right"><AlignRight className="w-4 h-4" /></ToolbarButton>
        <div className="w-px h-5 bg-stone-200 mx-1" />
        <ToolbarButton onClick={addLink} active={editor.isActive("link")} title="Insert link" testId="rte-link"><LinkIcon className="w-4 h-4" /></ToolbarButton>
        {!isDescription && (
          <ToolbarButton onClick={addImage} title="Insert image by URL" testId="rte-image"><ImageIcon className="w-4 h-4" /></ToolbarButton>
        )}
        <ToolbarButton onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Divider" testId="rte-hr"><Minus className="w-4 h-4" /></ToolbarButton>
        <div className="w-px h-5 bg-stone-200 mx-1" />
        <ToolbarButton onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo" testId="rte-undo"><Undo2 className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo" testId="rte-redo"><Redo2 className="w-4 h-4" /></ToolbarButton>
      </div>
      <EditorContent editor={editor} className={`tiptap-editor px-4 py-3 ${isDescription ? "min-h-[200px] max-h-[360px]" : "min-h-[280px] max-h-[480px]"} overflow-y-auto`} />
      {maxChars && (
        <div className={`px-3 py-1.5 text-[11px] flex justify-between border-t border-stone-200 bg-stone-50/40 rounded-b-lg ${atLimit ? "text-rose-600 font-semibold" : "text-stone-500"}`} data-testid="rte-char-count">
          <span>{atLimit ? `You have reached the maximum character limit (${maxChars} characters).` : ""}</span>
          <span>{charCount}/{maxChars} characters</span>
        </div>
      )}
    </div>
  );
}
